import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type MediaQuality = NonNullable<TargetingBaseType['mediaQuality']>;
type SectionState = { enabled: boolean };

// mediaQuality section -> permission that gives access to it
export const MEDIA_QUALITY_SECTION_PERMISSIONS = {
  adFraud: 'adFraud',
  attentionRateMeasurement: 'mediaQualityAttentionMetrics',
  brandSafety: 'brandSafety',
  viewabilityMeasurement: 'viewabilityPixelProvider',
  viewabilityPrebid: 'viewabilityThreshold'
} as const;

// What this user is allowed to see: sections enabled by someone else with more permissions are treated as off,
// so they never show up in the tags, the open state or the sidebar status. The entity itself is not modified.
export function getPermittedMediaQuality(
  mediaQuality: MediaQuality,
  user: UserInterface,
  userRoles: UserRoleInterface[]
): MediaQuality;
export function getPermittedMediaQuality(
  mediaQuality: MediaQuality | undefined,
  user: UserInterface,
  userRoles: UserRoleInterface[]
): MediaQuality | undefined;
export function getPermittedMediaQuality(
  mediaQuality: MediaQuality | undefined,
  user: UserInterface,
  userRoles: UserRoleInterface[]
): MediaQuality | undefined {
  if (!mediaQuality) {
    return mediaQuality;
  }
  const permitted: Record<string, unknown> = { ...mediaQuality };
  for (const [section, permission] of Object.entries(MEDIA_QUALITY_SECTION_PERMISSIONS)) {
    const state = permitted[section] as SectionState | undefined;
    if (state && !checkUserResourcePermissions(permission, user, userRoles)) {
      permitted[section] = { ...state, enabled: false };
    }
  }
  const anyPermittedSectionEnabled = Object.keys(MEDIA_QUALITY_SECTION_PERMISSIONS).some(
    section => (permitted[section] as SectionState | undefined)?.enabled
  );
  return { ...permitted, enabled: Boolean(anyPermittedSectionEnabled) } as MediaQuality;
}
